import { Link } from "react-router-dom";
import { ArrowRight, Hammer, Users, Sparkles, ShoppingBag, CalendarDays, HandHeart } from "lucide-react";
import { IMAGES, STAGE_FORMULES } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import { useLocale } from "@/context/LocaleContext";

const FORMULE_LIENS = {
  groupe: { to: "/offres/stage-fabrication-tambour#dates", cta: "Voir les prochaines dates", icon: Users },
  individuel: { to: "/offres/stage-fabrication-tambour#individuel", cta: "Découvrir les stages individuels et privatisés", icon: Hammer },
  editions: { to: "/tambours/editions-limitees", cta: "Découvrir les éditions spéciales", icon: Sparkles },
};
const fmtDate = (iso, locale) => new Intl.DateTimeFormat(locale === "en" ? "en-CH" : "fr-CH", { weekday: "short", day: "numeric", month: "long", year: "numeric" }).format(new Date(iso));

export const HomeStages = ({ events = [] }) => {
  const { locale, t } = useLocale();
  const dates = events.filter((e) => ["stage", "lieu_ancien"].includes(e.type) && !e.annule).slice(0, 3);
  return (
    <section id="tambours-stages" className="relative bg-earth grain text-sand overflow-hidden scroll-mt-20" data-testid="home-stages">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <Reveal className="lg:col-span-5">
          <div className="relative rounded-2xl overflow-hidden aspect-[4/5]">
            <img src={IMAGES.mains} alt={locale === "en" ? "Hands stretching a drum skin during a workshop in Orbe" : "Mains tendant la peau d'un tambour lors d'un stage de création à Orbe"} loading="lazy" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-earth/70 via-transparent to-transparent" />
            <p className="absolute bottom-5 left-5 right-5 font-mono text-[10px] uppercase tracking-[0.3em] text-sand/80">{t("Atelier d'Orbe · une journée · 10 h – 18 h")}</p>
          </div>
          <div className="mt-6 grid sm:grid-cols-3 gap-3 text-xs" data-testid="home-stages-distinction">
            <Link to="/offres/stage-fabrication-tambour" data-testid="home-stages-fabrique" className="rounded-xl border border-terra/60 bg-terra/10 p-4 hover:bg-terra/20 transition-colors duration-300">
              <Hammer className="w-4 h-4 text-terra mb-2" />
              <span className="block font-serif text-base leading-snug">{t("Je fabrique mon tambour lors d'un stage")}</span>
            </Link>
            <Link to="/boutique" data-testid="home-stages-achete" className="rounded-xl border border-sand/20 p-4 hover:border-sand/50 transition-colors duration-300">
              <ShoppingBag className="w-4 h-4 text-sand/70 mb-2" />
              <span className="block font-serif text-base leading-snug">{t("J'achète un tambour déjà réalisé")}</span>
            </Link>
            <Link to="/tambour-sur-mesure" data-testid="home-stages-sur-mesure" className="rounded-xl border border-sand/20 p-4 hover:border-terra hover:text-terra transition-colors duration-300">
              <HandHeart className="w-4 h-4 text-terra mb-2" />
              <span className="block font-serif text-base leading-snug">{t("Je confie sa création à Ludovic")}</span>
            </Link>
          </div>
        </Reveal>
        <div className="lg:col-span-7">
          <Reveal>
            <p className="eyebrow text-sand/70 mb-6 flex items-center gap-3"><Hammer className="w-4 h-4 text-terra" /> {t("Stages de création")}</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">{t("Créez votre propre tambour.")}</h2>
            <p className="text-sand/70 mt-6 max-w-xl leading-relaxed">{t("Une journée à l'atelier d'Orbe pour comprendre la matière — peau, cadre, laçage — et repartir avec un tambour fabriqué de vos mains, en petit groupe ou seul·e avec Ludovic.")}</p>
          </Reveal>
          <div className="mt-10 space-y-3">
            {STAGE_FORMULES.map((f, i) => {
              const L = FORMULE_LIENS[f.id];
              const vedette = f.id === "groupe";
              return (
                <Reveal key={f.id} delay={i * 0.08}>
                  <div className={`rounded-2xl border p-5 sm:p-6 grid sm:grid-cols-[1fr_auto] gap-4 items-center ${vedette ? "border-terra bg-sand/[0.06]" : "border-sand/15 bg-sand/[0.03]"}`} data-testid={`home-stages-${f.id}`}>
                    <div>
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <h3 className="font-serif text-2xl">{t(f.titre)}</h3>
                        <span className="font-mono text-sm text-terra">{f.prix}</span>
                      </div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand/50 mt-1">{t(f.badge)}</p>
                      {vedette && dates.length > 0 && (
                        <ul className="mt-4 flex flex-wrap gap-2" data-testid="home-stages-dates">
                          {dates.map((e) => (
                            <li key={e.id}>
                              <Link to={`/evenements/${e.slug}`} data-testid={`home-stages-date-${e.id.slice(0, 8)}`} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs transition-colors duration-300 ${e.places_restantes > 0 ? "border-sand/30 hover:border-terra hover:text-terra" : "border-sand/15 text-sand/50"}`}>
                                <CalendarDays className="w-3.5 h-3.5" />{fmtDate(e.date, locale)}{e.type === "lieu_ancien" ? ` · ${t("lieu ancien")}` : ""}{e.places_restantes > 0 ? ` · ${e.places_restantes} ${t("pl.")}` : ` · ${t("complet")}`}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <Link to={L.to} data-testid={`home-stages-cta-${f.id}`} className={`group inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm whitespace-nowrap transition-colors duration-500 ${vedette ? "bg-terra text-earth hover:bg-sand" : "border border-sand/30 hover:border-sand hover:bg-sand/10"}`}>
                      {t(L.cta)} <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
